import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { getModalSelector } from '@store/modals';
import { ModalsService, PopupsService } from '@services';
import { Modals, Popups } from '@enums';
import { Button, Icon, Modal } from '@elements';
import { pxToRem } from '@themes';
import { StyledButtonsContainer, StyledChange, StyledChangesContainer, StyledDivider, StyledListTypographyContainer, StyledNewListChanges, StyledTypography } from './users-conflicts.styles';
import acceptedIcon from '@images/accepted.svg';
import arrowLeft from '@images/arrow-left.svg';
import declinedIcon from '@images/declined.svg';
import close from '@images/delete.svg';
import warning from '@images/warning.svg';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

/**
 * User conflicts modal component.
 *
 * @author Ihar Kazlouski
 * @function UsersConflictsModal
 * @category Modals
 * @return {FC} User conflicts modal component.
 */
var UsersConflictsModal = function UsersConflictsModal() {
  var modal = useSelector(getModalSelector(Modals.UsersConflicts));
  var modalData = modal === null || modal === void 0 ? void 0 : modal.data;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      acceptedChanges = _useState2[0],
      setAcceptedChanges = _useState2[1];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      acceptedIdx = _useState4[0],
      setAcceptedIdx = _useState4[1];

  var isAnyConflictsLeft = modalData.conflicts.length !== acceptedChanges.length;
  /**
   * Accept all changes.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */

  var handleAcceptAll = function handleAcceptAll() {
    setAcceptedChanges(modalData.conflicts.map(function (conflict) {
      return conflict.newChanges;
    }));
    setAcceptedIdx(modalData.conflicts.map(function (conflict, idx) {
      return {
        idx: idx,
        status: 'accepted'
      };
    }));
  };
  /**
   * Decline all changes.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleDeclineAll = function handleDeclineAll() {
    setAcceptedChanges(modalData.conflicts.map(function (conflict) {
      return conflict.current;
    }));
    setAcceptedIdx(modalData.conflicts.map(function (conflict, idx) {
      return {
        idx: idx,
        status: 'declined'
      };
    }));
  };
  /**
   * Undo all changes.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleUndoAll = function handleUndoAll() {
    setAcceptedChanges([]);
    setAcceptedIdx([]);
  };
  /**
   * Accept change.
   *
   * @author Ihar Kazlouski
   * @param {AcceptedFields} changes object with changes.
   * @param {idx} idx index.
   * @return {void}
   */


  var handleAccept = function handleAccept(changes, idx) {
    return function () {
      if (!acceptedIdx.find(function (item) {
        return item.idx === idx;
      })) {
        setAcceptedIdx(function (prevState) {
          return [].concat(_toConsumableArray(prevState), [{
            idx: idx,
            status: 'accepted'
          }]);
        });
        setAcceptedChanges(function (prevState) {
          return [].concat(_toConsumableArray(prevState), [changes]);
        });
      }
    };
  };
  /**
   * Decline change.
   *
   * @author Ihar Kazlouski
   * @param {AcceptedFields} changes object with changes.
   * @param {idx} idx index.
   * @return {void}
   */


  var handleDecline = function handleDecline(changes, idx) {
    return function () {
      if (!acceptedIdx.find(function (item) {
        return item.idx === idx;
      })) {
        setAcceptedIdx(function (prevState) {
          return [].concat(_toConsumableArray(prevState), [{
            idx: idx,
            status: 'declined'
          }]);
        });
        setAcceptedChanges(function (prevState) {
          return [].concat(_toConsumableArray(prevState), [changes]);
        });
      }
    };
  };
  /**
   * Undo action.
   *
   * @author Ihar Kazlouski
   * @param {string} userId user id.
   * @param {idx} idx index.
   * @return {void}
   */


  var handleUndo = function handleUndo(userId, idx) {
    return function () {
      setAcceptedIdx(function (prevState) {
        return prevState.filter(function (item) {
          return item.idx !== idx;
        });
      });
      setAcceptedChanges(function (prevState) {
        return prevState.filter(function (item) {
          return item.user_id !== userId;
        });
      });
    };
  };
  /**
   * Close modal.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var closeModal = function closeModal() {
    PopupsService.open(Popups.Default, {
      header: t('components.popups.unsavedChanges'),
      body: t('components.modals.uploadAllUsers.quit'),
      acceptButton: t('components.popups.yes'),
      rejectButton: t('components.popups.cancel'),
      maxWidth: 'xs',
      width: 270
    }).then(function () {
      return ModalsService.close(Modals.UsersConflicts);
    })["catch"](function () {// error handling
    });
  };
  /**
   * Submit users.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleSubmit = function handleSubmit() {
    PopupsService.open(Popups.Default, {
      header: '',
      body: t('components.modals.uploadAllUsers.uploadUsers'),
      acceptButton: t('components.popups.yes'),
      rejectButton: t('components.popups.cancel'),
      maxWidth: 'xs',
      width: 270
    }).then(function () {
      ModalsService.close(Modals.UsersConflicts);
    })["catch"](function () {// error handling
    });
  };
  /**
   * Return to previous modal.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleReturn = function handleReturn() {
    ModalsService.close(Modals.UsersConflicts);
    void ModalsService.open(Modals.AddUsersWithFile, {
      fileName: modalData === null || modalData === void 0 ? void 0 : modalData.fileName,
      formData: modalData === null || modalData === void 0 ? void 0 : modalData.formData
    });
  };

  return /*#__PURE__*/_jsxs(Modal, {
    fullWidth: true,
    maxWidth: "xxl",
    adminModal: true,
    children: [/*#__PURE__*/_jsx(Modal.Title, {
      handleClose: closeModal,
      children: /*#__PURE__*/_jsxs(Box, {
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        paddingY: 2.5,
        paddingX: 2.5,
        children: [/*#__PURE__*/_jsx(IconButton, {
          onClick: handleReturn,
          size: "small",
          children: /*#__PURE__*/_jsx(Icon, {
            src: arrowLeft,
            width: 24,
            height: 24,
            color: "comet"
          })
        }), /*#__PURE__*/_jsx(IconButton, {
          onClick: closeModal,
          size: "small",
          children: /*#__PURE__*/_jsx(Icon, {
            src: close,
            width: 24,
            height: 24,
            color: "comet"
          })
        })]
      })
    }), /*#__PURE__*/_jsx(Modal.Content, {
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Box, {
          children: [/*#__PURE__*/_jsx(StyledDivider, {}), /*#__PURE__*/_jsxs(Grid, {
            container: true,
            justifyContent: "space-between",
            alignItems: "center",
            paddingY: 7.5,
            paddingX: 5,
            children: [/*#__PURE__*/_jsx(Grid, {
              item: true,
              xs: 6,
              children: /*#__PURE__*/_jsx(Typography, {
                variant: "h3",
                fontWeight: "bold",
                color: "mineShaft",
                children: modalData.fileName
              })
            }), /*#__PURE__*/_jsx(Grid, {
              item: true,
              xs: 6,
              display: "flex",
              justifyContent: "end",
              children: /*#__PURE__*/_jsx(Button, {
                variant: "contained",
                disabled: isAnyConflictsLeft,
                onClick: handleSubmit,
                sx: {
                  padding: "".concat(pxToRem(14), " ").concat(pxToRem(28))
                },
                children: t('components.modals.uploadAllUsersConflicts.submit')
              })
            })]
          }), /*#__PURE__*/_jsx(StyledDivider, {}), /*#__PURE__*/_jsxs(Grid, {
            container: true,
            justifyContent: "space-between",
            alignItems: "center",
            paddingY: 3.5,
            paddingX: 5,
            children: [/*#__PURE__*/_jsx(Grid, {
              item: true,
              xs: 8,
              children: /*#__PURE__*/_jsxs(Box, {
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                children: [/*#__PURE__*/_jsx(Icon, {
                  src: isAnyConflictsLeft ? warning : acceptedIcon,
                  width: 24,
                  height: 24,
                  color: "comet"
                }), /*#__PURE__*/_jsx(Typography, {
                  variant: "body1",
                  fontWeight: "bold",
                  color: "mineShaft",
                  children: isAnyConflictsLeft ? t('components.modals.uploadAllUsersConflicts.notice') : t('components.modals.uploadAllUsersConflicts.conflictsResolved')
                })]
              })
            }), isAnyConflictsLeft ? /*#__PURE__*/_jsx(Grid, {
              item: true,
              xs: 4,
              justifyContent: "end",
              display: "flex",
              children: /*#__PURE__*/_jsxs(Box, {
                display: "flex",
                alignItems: "center",
                gap: 2,
                children: [/*#__PURE__*/_jsx(Button, {
                  variant: "contained",
                  color: "secondary",
                  onClick: handleDeclineAll,
                  children: t('components.modals.uploadAllUsersConflicts.declineAll')
                }), /*#__PURE__*/_jsx(Button, {
                  variant: "contained",
                  onClick: handleAcceptAll,
                  children: t('components.modals.uploadAllUsersConflicts.acceptAll')
                })]
              })
            }) : /*#__PURE__*/_jsx(Button, {
              color: "comet",
              variant: "outlined",
              onClick: handleUndoAll,
              children: t('components.modals.uploadAllUsersConflicts.undoAll')
            })]
          }), /*#__PURE__*/_jsx(StyledDivider, {})]
        }), /*#__PURE__*/_jsx(StyledChangesContainer, {
          children: modalData === null || modalData === void 0 ? void 0 : modalData.conflicts.map(function (conflict, index) {
            var item = acceptedIdx.find(function (idx) {
              return idx.idx === index;
            });
            return /*#__PURE__*/_jsxs(Box, {
              pt: 6.375,
              children: [/*#__PURE__*/_jsx(StyledDivider, {}), /*#__PURE__*/_jsxs(StyledChange, {
                container: true,
                children: [/*#__PURE__*/_jsxs(Grid, {
                  item: true,
                  container: true,
                  xs: 10,
                  display: "flex",
                  flexDirection: "column",
                  children: [/*#__PURE__*/_jsxs(Grid, {
                    item: true,
                    children: [/*#__PURE__*/_jsx(StyledListTypographyContainer, {
                      pt: 2,
                      pb: 1,
                      pl: 3.75,
                      children: /*#__PURE__*/_jsxs(Typography, {
                        variant: "body2",
                        fontWeight: "400",
                        color: "comet",
                        children: [t('components.modals.uploadAllUsersConflicts.newList'), ' ', "(", modalData.fileName, "):"]
                      })
                    }), /*#__PURE__*/_jsxs(StyledNewListChanges, {
                      children: [/*#__PURE__*/_jsx(StyledTypography, {
                        highlighted: !!conflict.conflictFields.find(function (field) {
                          return field === 'first_name';
                        }),
                        variant: "body1",
                        fontWeight: "500",
                        color: "comet",
                        children: conflict.newChanges.first_name
                      }), /*#__PURE__*/_jsx(StyledTypography, {
                        highlighted: !!conflict.conflictFields.find(function (field) {
                          return field === 'last_name';
                        }),
                        variant: "body1",
                        fontWeight: "500",
                        color: "comet",
                        children: conflict.newChanges.last_name
                      }), /*#__PURE__*/_jsx(StyledTypography, {
                        highlighted: !!conflict.conflictFields.find(function (field) {
                          return field === 'email';
                        }),
                        variant: "body1",
                        fontWeight: "500",
                        color: "comet",
                        children: conflict.newChanges.email
                      }), /*#__PURE__*/_jsx(StyledTypography, {
                        highlighted: !!conflict.conflictFields.find(function (field) {
                          return field === 'phone_number';
                        }),
                        variant: "body1",
                        fontWeight: "500",
                        color: "comet",
                        children: conflict.newChanges.phone_number
                      })]
                    })]
                  }), /*#__PURE__*/_jsxs(Grid, {
                    item: true,
                    children: [/*#__PURE__*/_jsx(StyledListTypographyContainer, {
                      pt: 2,
                      pb: 1,
                      pl: 3.75,
                      children: /*#__PURE__*/_jsx(Typography, {
                        variant: "body2",
                        fontWeight: "400",
                        color: "comet",
                        children: t('components.modals.uploadAllUsersConflicts.currentList')
                      })
                    }), /*#__PURE__*/_jsxs(StyledNewListChanges, {
                      children: [/*#__PURE__*/_jsx(StyledTypography, {
                        highlighted: !!conflict.conflictFields.find(function (field) {
                          return field === 'first_name';
                        }),
                        variant: "body1",
                        fontWeight: "500",
                        color: "comet",
                        children: conflict.current.first_name
                      }), /*#__PURE__*/_jsx(StyledTypography, {
                        highlighted: !!conflict.conflictFields.find(function (field) {
                          return field === 'last_name';
                        }),
                        variant: "body1",
                        fontWeight: "500",
                        color: "comet",
                        children: conflict.current.last_name
                      }), /*#__PURE__*/_jsx(StyledTypography, {
                        highlighted: !!conflict.conflictFields.find(function (field) {
                          return field === 'email';
                        }),
                        variant: "body1",
                        fontWeight: "500",
                        color: "comet",
                        children: conflict.current.email
                      }), /*#__PURE__*/_jsx(StyledTypography, {
                        highlighted: !!conflict.conflictFields.find(function (field) {
                          return field === 'phone_number';
                        }),
                        variant: "body1",
                        fontWeight: "500",
                        color: "comet",
                        children: conflict.current.phone_number
                      })]
                    })]
                  })]
                }), /*#__PURE__*/_jsx(Grid, {
                  item: true,
                  xs: 2,
                  children: !item ? /*#__PURE__*/_jsxs(StyledButtonsContainer, {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2.5,
                    paddingY: 11,
                    children: [/*#__PURE__*/_jsx(Button, {
                      variant: "contained",
                      size: "small",
                      onClick: handleAccept(conflict.newChanges, index),
                      children: t('components.modals.uploadAllUsersConflicts.accept')
                    }), /*#__PURE__*/_jsx(Button, {
                      variant: "contained",
                      color: "secondary",
                      size: "small",
                      onClick: handleDecline(conflict.current, index),
                      children: t('components.modals.uploadAllUsersConflicts.decline')
                    })]
                  }) : /*#__PURE__*/_jsxs(StyledButtonsContainer, {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2.5,
                    paddingY: 8.75,
                    children: [/*#__PURE__*/_jsx(Icon, {
                      src: item.status === 'accepted' ? acceptedIcon : declinedIcon,
                      width: 40,
                      height: 40
                    }), /*#__PURE__*/_jsx(Typography, {
                      variant: "body1",
                      fontWeight: "bold",
                      children: item.status === 'accepted' ? t('components.modals.uploadAllUsersConflicts.changesAccepted') : t('components.modals.uploadAllUsersConflicts.changesDeclined')
                    }), /*#__PURE__*/_jsx(Button, {
                      color: "comet",
                      variant: "outlined",
                      size: "small",
                      sx: {
                        width: '120px'
                      },
                      onClick: handleUndo(conflict.current.user_id, index),
                      children: t('components.modals.uploadAllUsersConflicts.undo')
                    })]
                  })
                })]
              }), /*#__PURE__*/_jsx(StyledDivider, {})]
            }, conflict.id);
          })
        })]
      })
    })]
  });
};

export { UsersConflictsModal };